'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface BenchmarkData {
  category: string;
  industry: number;
  venkaLabs: number;
}

interface BenchmarkSectionProps {
  title: string;
  description: string;
  data: BenchmarkData[];
}

const BenchmarkSection = ({ title, description, data }: BenchmarkSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
              {title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {description}
            </p>
          </div>
          <div className="bg-card rounded-lg p-8 shadow-md">
            <div className="w-full h-96 flex items-center justify-center">
              <Icon name="ChartBarIcon" size={48} variant="outline" className="text-muted-foreground animate-pulse" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Icon name="ChartBarIcon" size={32} variant="solid" className="text-accent" />
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground">
              {title}
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {description}
          </p>
        </div>
        
        <div className="bg-card rounded-lg p-8 shadow-md">
          <div className="w-full h-96" aria-label="Industry Benchmark Comparison Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="category" 
                  tick={{ fill: '#5D6D7E', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E7EB' }}
                />
                <YAxis 
                  tick={{ fill: '#5D6D7E', fontSize: 12 }}
                  axisLine={{ stroke: '#E5E7EB' }}
                  label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', fill: '#5D6D7E' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                />
                <Bar 
                  dataKey="industry" 
                  fill="#2B5A87" 
                  name="Industry Average"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="venkaLabs" 
                  fill="#E67E22" 
                  name="Invictosoft Solutions"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-headline font-bold text-accent mb-2">35%</div>
              <div className="text-sm text-muted-foreground">Average Performance Improvement</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-headline font-bold text-success mb-2">2.5x</div>
              <div className="text-sm text-muted-foreground">Faster Implementation Time</div>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="text-3xl font-headline font-bold text-primary mb-2">98%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenchmarkSection;